import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  GameSession,
  GameEvent,
  Player,
  Portfolio,
  TwitterContext,
  GeneratedSituation,
  GeneratedOutcome,
  ConsequenceLog,
  PlayerAction,
  GeneratedImage,
  Storyline,
} from '@/types'

interface GameState {
  // Current game session
  session: GameSession | null

  // Event queue (legacy)
  currentEvent: GameEvent | null
  eventHistory: GameEvent[]

  // AI Game Master state
  currentSituation: GeneratedSituation | null
  currentOutcome: GeneratedOutcome | null
  currentSceneImage: GeneratedImage | null
  consequenceLog: ConsequenceLog[]
  recentActions: PlayerAction[]
  generatedSituationIds: string[]
  isGeneratingSituation: boolean
  isGeneratingOutcome: boolean

  // Twitter context
  twitterContext: TwitterContext | null

  // UI State
  isLoading: boolean
  error: string | null
  showSettings: boolean
  crtEffectEnabled: boolean
  soundEnabled: boolean
  decisionTimerEnabled: boolean
  voiceModeEnabled: boolean

  // Actions
  setSession: (session: GameSession | null) => void
  updatePlayer: (player: Partial<Player>) => void
  updatePortfolio: (portfolio: Partial<Portfolio>) => void
  setCurrentEvent: (event: GameEvent | null) => void
  addToEventHistory: (event: GameEvent) => void
  setTwitterContext: (context: TwitterContext | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  toggleSettings: () => void
  toggleCRTEffect: () => void
  toggleSound: () => void
  toggleDecisionTimer: () => void
  toggleVoiceMode: () => void
  resetGame: () => void

  // Game Master actions
  setCurrentSituation: (situation: GeneratedSituation | null) => void
  setCurrentOutcome: (outcome: GeneratedOutcome | null) => void
  setCurrentSceneImage: (image: GeneratedImage | null) => void
  addConsequence: (consequence: ConsequenceLog) => void
  resolveConsequence: (consequenceId: string) => void
  addPlayerAction: (action: PlayerAction) => void
  addGeneratedSituationId: (id: string) => void
  setGeneratingSituation: (generating: boolean) => void
  setGeneratingOutcome: (generating: boolean) => void
  clearSituationState: () => void

  // Storyline state
  activeStorylines: Storyline[]
  completedStorylines: Storyline[]
  currentStorylineId: string | null

  // Storyline actions
  addStoryline: (storyline: Storyline) => void
  updateStoryline: (storylineId: string, updates: Partial<Storyline>) => void
  setCurrentStoryline: (storylineId: string | null) => void
  completeStoryline: (storylineId: string, status: 'completed' | 'failed' | 'abandoned') => void
  addSituationToStoryline: (storylineId: string, situationId: string) => void
}

const initialState = {
  session: null,
  currentEvent: null,
  eventHistory: [],
  twitterContext: null,
  isLoading: false,
  error: null,
  showSettings: false,
  crtEffectEnabled: true,
  soundEnabled: true,
  decisionTimerEnabled: false,
  voiceModeEnabled: false,
  // Game Master state
  currentSituation: null,
  currentOutcome: null,
  currentSceneImage: null,
  consequenceLog: [],
  recentActions: [],
  generatedSituationIds: [],
  isGeneratingSituation: false,
  isGeneratingOutcome: false,
  // Storyline state
  activeStorylines: [] as Storyline[],
  completedStorylines: [] as Storyline[],
  currentStorylineId: null as string | null,
}

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setSession: (session) => set({ session }),

      updatePlayer: (playerUpdate) => {
        const { session } = get()
        if (!session) return
        set({
          session: {
            ...session,
            player: { ...session.player, ...playerUpdate },
          },
        })
      },

      updatePortfolio: (portfolioUpdate) => {
        const { session } = get()
        if (!session) return
        set({
          session: {
            ...session,
            player: {
              ...session.player,
              portfolio: { ...session.player.portfolio, ...portfolioUpdate },
            },
          },
        })
      },

      setCurrentEvent: (event) => set({ currentEvent: event }),

      addToEventHistory: (event) => {
        const { eventHistory } = get()
        set({ eventHistory: [...eventHistory, event] })
      },

      setTwitterContext: (context) => set({ twitterContext: context }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      toggleSettings: () => set((state) => ({ showSettings: !state.showSettings })),

      toggleCRTEffect: () =>
        set((state) => ({ crtEffectEnabled: !state.crtEffectEnabled })),

      toggleSound: () => set((state) => ({ soundEnabled: !state.soundEnabled })),

      toggleDecisionTimer: () => set((state) => ({ decisionTimerEnabled: !state.decisionTimerEnabled })),

      toggleVoiceMode: () => set((state) => ({ voiceModeEnabled: !state.voiceModeEnabled })),

      resetGame: () => set({
        ...initialState,
        crtEffectEnabled: get().crtEffectEnabled,
        soundEnabled: get().soundEnabled,
        decisionTimerEnabled: get().decisionTimerEnabled,
        voiceModeEnabled: get().voiceModeEnabled,
      }),

      // Game Master actions
      setCurrentSituation: (situation) => set({ currentSituation: situation, currentOutcome: null }),

      setCurrentOutcome: (outcome) => set({ currentOutcome: outcome }),

      setCurrentSceneImage: (image) => set({ currentSceneImage: image }),

      addConsequence: (consequence) => {
        const { consequenceLog } = get()
        set({ consequenceLog: [...consequenceLog, consequence] })
      },

      resolveConsequence: (consequenceId) => {
        const { consequenceLog } = get()
        set({
          consequenceLog: consequenceLog.map((c) =>
            c.id === consequenceId ? { ...c, resolved: true } : c
          ),
        })
      },

      addPlayerAction: (action) => {
        const { recentActions } = get()
        // Keep last 20 actions
        const updated = [action, ...recentActions].slice(0, 20)
        set({ recentActions: updated })
      },

      addGeneratedSituationId: (id) => {
        const { generatedSituationIds } = get()
        set({ generatedSituationIds: [...generatedSituationIds, id] })
      },

      setGeneratingSituation: (generating) => set({ isGeneratingSituation: generating }),

      setGeneratingOutcome: (generating) => set({ isGeneratingOutcome: generating }),

      clearSituationState: () => set({
        currentSituation: null,
        currentOutcome: null,
        currentSceneImage: null,
      }),

      // Storyline actions
      addStoryline: (storyline) => {
        const { activeStorylines } = get()
        set({ activeStorylines: [...activeStorylines, storyline] })
      },

      updateStoryline: (storylineId, updates) => {
        const { activeStorylines } = get()
        set({
          activeStorylines: activeStorylines.map((s) =>
            s.id === storylineId ? { ...s, ...updates, updatedAt: new Date() } : s
          ),
        })
      },

      setCurrentStoryline: (storylineId) => set({ currentStorylineId: storylineId }),

      completeStoryline: (storylineId, status) => {
        const { activeStorylines, completedStorylines } = get()
        const storyline = activeStorylines.find((s) => s.id === storylineId)
        if (!storyline) return

        const completed: Storyline = {
          ...storyline,
          status,
          updatedAt: new Date(),
        }

        set({
          activeStorylines: activeStorylines.filter((s) => s.id !== storylineId),
          completedStorylines: [...completedStorylines, completed],
          currentStorylineId: null,
        })
      },

      addSituationToStoryline: (storylineId, situationId) => {
        const { activeStorylines } = get()
        set({
          activeStorylines: activeStorylines.map((s) =>
            s.id === storylineId
              ? { ...s, situationIds: [...s.situationIds, situationId], updatedAt: new Date() }
              : s
          ),
        })
      },
    }),
    {
      name: 'trench-quest-storage',
      partialize: (state) => ({
        crtEffectEnabled: state.crtEffectEnabled,
        soundEnabled: state.soundEnabled,
        decisionTimerEnabled: state.decisionTimerEnabled,
        voiceModeEnabled: state.voiceModeEnabled,
        // Don't persist game session - that goes to Firebase
      }),
    }
  )
)

// Selector hooks for common patterns
export const useCurrentSession = () => useGameStore((state) => state.session)
export const useCurrentEvent = () => useGameStore((state) => state.currentEvent)
export const useIsLoading = () => useGameStore((state) => state.isLoading)
export const useError = () => useGameStore((state) => state.error)

// Game Master selector hooks
export const useCurrentSituation = () => useGameStore((state) => state.currentSituation)
export const useCurrentOutcome = () => useGameStore((state) => state.currentOutcome)
export const useCurrentSceneImage = () => useGameStore((state) => state.currentSceneImage)
export const useConsequenceLog = () => useGameStore((state) => state.consequenceLog)
export const useRecentActions = () => useGameStore((state) => state.recentActions)
export const useIsGeneratingSituation = () => useGameStore((state) => state.isGeneratingSituation)
export const useIsGeneratingOutcome = () => useGameStore((state) => state.isGeneratingOutcome)

// Storyline selector hooks
export const useActiveStorylines = () => useGameStore((state) => state.activeStorylines)
export const useCompletedStorylines = () => useGameStore((state) => state.completedStorylines)
export const useCurrentStoryline = () => {
  const activeStorylines = useGameStore((state) => state.activeStorylines)
  const currentStorylineId = useGameStore((state) => state.currentStorylineId)
  return activeStorylines.find((s) => s.id === currentStorylineId) || null
}
